import { useCallback, useReducer } from 'react';
import { isEqual } from 'es-toolkit';

import type { CategoryContext } from '../../achievement-standard-picker/types';
import type { SearchFilter } from '../types';
import {
  getSchoolTypeFromGradeIds,
  shouldExcludeSubject,
} from '../utils/subjectFilters';

import type { GradeOption } from './useGradeOptions';

/**
 * 검색 필터 상태
 */
type SearchFilterState = {
  keyword: string;
  gradeOption: GradeOption | null;
  subjectGroupId: string | null;
  lastSearchedKeyword: string;
  lastSearchedCategoryIds: string[] | undefined;
};

/**
 * 검색 필터 액션
 */
type SearchFilterAction =
  | { type: 'KEYWORD_CHANGE'; payload: string }
  | { type: 'GRADE_CHANGE'; payload: GradeOption }
  | { type: 'SUBJECT_CHANGE'; payload: string }
  | { type: 'SEARCH_SUBMIT'; payload: { categoryIds: string[] | undefined } }
  | { type: 'SEARCH_CLEAR' };

/**
 * createInitialState - 초기 상태 생성
 *
 * 필터 값은 항상 기본값("전체 학년", "전체 교과")을 가짐.
 * 초기 과목이 성취기준이 없는 과목인 경우 'all'로 fallback하여 "전체 교과" 선택
 */
function createInitialState(
  gradeOptions: GradeOption[],
  categoryContext?: CategoryContext,
): SearchFilterState {
  const initialGradeOption = gradeOptions.find(option =>
    option.ids.includes(categoryContext?.initialSchoolInfoId || ''),
  );

  // 학년이 없으면 과목도 무효 → 기본값 'all' (전체 교과)
  const initialSubjectId = categoryContext?.initialSubjectId;
  let subjectGroupId =
    initialGradeOption && initialSubjectId ? initialSubjectId : 'all';

  if (initialSubjectId && initialGradeOption && categoryContext?.subjectTree) {
    const schoolType = getSchoolTypeFromGradeIds(initialGradeOption.ids);
    const subject = categoryContext.subjectTree.find(
      s => s.categoryGroupId === initialSubjectId,
    );

    if (subject && shouldExcludeSubject(schoolType, subject.name)) {
      // Fallback to "전체 교과" if initial subject is excluded
      subjectGroupId = 'all';
    }
  }

  return {
    keyword: '',
    gradeOption: initialGradeOption ?? gradeOptions[0] ?? null,
    subjectGroupId,
    lastSearchedKeyword: '',
    lastSearchedCategoryIds: undefined,
  };
}

/**
 * 검색 필터 리듀서
 */
function searchFilterReducer(
  state: SearchFilterState,
  action: SearchFilterAction,
): SearchFilterState {
  switch (action.type) {
    case 'KEYWORD_CHANGE':
      return {
        ...state,
        keyword: action.payload,
      };

    case 'GRADE_CHANGE':
      // 학년 옵션 변경 시 과목 초기화 → 'all' (전체 교과)
      return {
        ...state,
        gradeOption: action.payload,
        subjectGroupId: 'all',
      };

    case 'SUBJECT_CHANGE':
      return { ...state, subjectGroupId: action.payload };

    case 'SEARCH_SUBMIT':
      return {
        ...state,
        lastSearchedKeyword: state.keyword,
        lastSearchedCategoryIds: action.payload.categoryIds,
      };

    case 'SEARCH_CLEAR':
      return {
        ...state,
        keyword: '',
        lastSearchedKeyword: '',
        lastSearchedCategoryIds: undefined,
      };

    default:
      return state;
  }
}

/**
 * categoryIds 계산 헬퍼 함수
 * 학년 IDs와 과목 ID를 조합하여 Neo4j 필터용 categoryIds 배열 생성
 *
 * @param gradeIds - 학년 IDs (5자리)
 * @param subjectGroupId - 과목 그룹 ID (2자리) 또는 'all'
 * @returns categoryIds 배열 또는 undefined
 */
function calculateCategoryIds(
  gradeIds: string[],
  subjectGroupId: string | null,
): string[] | undefined {
  if (gradeIds.length === 0) return undefined;

  // 과목이 선택되고 'all'이 아닌 경우: 7자리 (학년 5자 + 과목 2자)
  if (subjectGroupId && subjectGroupId !== 'all') {
    return gradeIds.map(gradeId => `${gradeId}${subjectGroupId}`);
  }

  // 과목이 선택되지 않거나 'all'인 경우: 5자리 (학년만)
  return gradeIds;
}

/**
 * 검색 필터 상태 관리 훅
 * useReducer를 사용하여 keyword, grade, subject 상태 관리
 *
 * @param gradeOptions - 학년 옵션 목록
 * @param onSearch - 검색 실행 콜백
 * @param categoryContext - 카테고리 컨텍스트 (초기값 설정용)
 */
export const useSearchFilter = (
  gradeOptions: GradeOption[],
  onSearch: (filter: SearchFilter) => void,
  categoryContext?: CategoryContext,
) => {
  const [state, dispatch] = useReducer(searchFilterReducer, undefined, () =>
    createInitialState(gradeOptions, categoryContext),
  );

  const handleKeywordChange = useCallback((keyword: string) => {
    dispatch({ type: 'KEYWORD_CHANGE', payload: keyword });
  }, []);

  const handleGradeChange = useCallback((gradeOption: GradeOption) => {
    dispatch({ type: 'GRADE_CHANGE', payload: gradeOption });
  }, []);

  const handleSubjectChange = useCallback((subjectGroupId: string) => {
    dispatch({ type: 'SUBJECT_CHANGE', payload: subjectGroupId });
  }, []);

  const handleSearchSubmit = useCallback(() => {
    const gradeIds = state.gradeOption?.ids ?? [];
    const categoryIds = calculateCategoryIds(gradeIds, state.subjectGroupId);
    dispatch({ type: 'SEARCH_SUBMIT', payload: { categoryIds } });
    onSearch({
      query: state.keyword,
      categoryIds,
    });
  }, [state.keyword, state.gradeOption, state.subjectGroupId, onSearch]);

  const handleSearchClear = useCallback(() => {
    dispatch({ type: 'SEARCH_CLEAR' });
  }, []);

  // 계산된 값들
  // ids가 비어있으면 "전체 학년" (객체 참조 비교는 gradeOptions 재생성 시 불안정)
  const isSubjectDisabled =
    !state.gradeOption || state.gradeOption.ids.length === 0;

  const currentCategoryIds = calculateCategoryIds(
    state.gradeOption?.ids ?? [],
    state.subjectGroupId,
  );

  // 키워드 변경 여부 (검색 버튼/Enter 키 표시 조건)
  const hasKeywordChanged = !isEqual(state.keyword, state.lastSearchedKeyword);
  const isDirty =
    hasKeywordChanged ||
    !isEqual(currentCategoryIds, state.lastSearchedCategoryIds);

  return {
    state: {
      ...state,
      isSubjectDisabled,
      hasKeywordChanged,
      isDirty,
    },
    handleKeywordChange,
    handleGradeChange,
    handleSubjectChange,
    handleSearchSubmit,
    handleSearchClear,
  };
};
